import flow from 'lodash/fp/flow';
import pick from 'lodash/fp/pick';
import get from 'lodash/fp/get';
import mapValues from 'lodash/fp/mapValues';
import sanitizeHtml from 'sanitize-html';
import uniq from 'lodash/uniq';
import { generateLazyLoadEmbed, isYoutubeEmbedLink } from './youtube';

// All content item fields that can contain HTML with script tags.
const contentFields = ['content', 'excerpt'];

// TODO: remove when USATECH-1712 gets addressed
// Some of our embeds for e.g. instagram do not include the protocol in their URL.
// Sanitize-html considers this to be invalid and fully removes them automatically.
// As a temporary solution, we are patching this here until we can try to address it
// in Wordpress in
// https://trello.com/c/6m99Z7ec/1712-spike-investigation-on-whether-we-could-solve-some-embeds-problems-in-wordpress-instead-of-the-frontend
// The regexp below matches a protocol-less URL inside of a src tag and adds https as the protocol
const addMissingHttpsProtocolToEmbedsURLs = (content) => content.replace(
  /"(\/\/(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]+[^"]*)"/g,
  'https:$1',
);

const getUniqueValidScriptsSrc = (scripts) => uniq(
  scripts.filter(Boolean).map(get(['attribs', 'src'])),
).filter(Boolean);

/**
 * Filter any script tags found. Add filtered scripts to collection.
 * For more documentation on this see docs/article-content-embeds.md
 *
 * @param {string} content
 * @return {string}
 */
const filterContentFields = (contentItem) => {
  const scripts = [];
  const lazyLoadEmbeds = {};

  const filterContent = (content) => {
    const lazyLoadReplaceMap = {};
    const toBeReplacedTagName = 'to-be-replaced-lazy-load-facade';
    const sanitizedHtml = sanitizeHtml(
      addMissingHttpsProtocolToEmbedsURLs(content), {
        allowedTags: false,
        allowedAttributes: false,
        allowVulnerableTags: true,
        exclusiveFilter: (frame) => {
          if ('script' !== frame.tag) {
            return false;
          }
          scripts.push(frame);
          return true;
        },
        transformTags: {
          iframe: (tagName, attribs) => {
            if (isYoutubeEmbedLink(attribs.src)) {
              const {
                videoId,
                lazyLoadFacade,
                iframe,
              } = generateLazyLoadEmbed({ tagName, attribs });
              lazyLoadEmbeds[videoId] = iframe;
              lazyLoadReplaceMap[videoId] = lazyLoadFacade;
              return {
                tagName: toBeReplacedTagName,
                attribs: {
                  'data-id': videoId,
                },
              };
            }
            return {
              tagName,
              attribs,
            };
          },
        },
      },
    );
    return Object.keys(lazyLoadReplaceMap).reduce((html, id) => {
      const regExp = new RegExp(`<${toBeReplacedTagName} data-id="${id}"></${toBeReplacedTagName}>`, 'g');
      return html.replace(regExp, lazyLoadReplaceMap[id]);
    }, sanitizedHtml);
  };

  const filteredContent = flow(
    pick(contentFields),
    mapValues(filterContent),
  )(contentItem);

  return {
    filteredContent,
    lazyLoadEmbeds,
    scripts: getUniqueValidScriptsSrc(scripts),
  };
};

export default filterContentFields;
